// src/context/NotificationContext.js
import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
} from "react";
import {
  getNotificationsApi,
  getUnreadCountApi,
  markReadApi,
  markAllReadApi,
  deleteNotificationApi,
  clearAllNotificationsApi,
} from "../services/axios/AuthApis/NotificationApis";

const NotificationContext = createContext();

const POLL_INTERVAL_MS = 30_000; // poll unread count every 30 s

export const NotificationProvider = ({ children, user }) => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [panelOpen, setPanelOpen] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    page_size: 20,
    total_count: 0,
    total_pages: 1,
    has_next: false,
    has_previous: false,
  });

  const userId = user?.id || user?._id || null;
  const pollRef = useRef(null);

  // ── polling: refresh unread count badge ──────────────────────────
  const refreshUnreadCount = useCallback(async () => {
    if (!userId) return;
    try {
      const count = await getUnreadCountApi(userId);
      setUnreadCount(count);
    } catch (_) {
      /* silent */
    }
  }, [userId]);

  useEffect(() => {
    if (!userId) return;
    refreshUnreadCount();
    pollRef.current = setInterval(refreshUnreadCount, POLL_INTERVAL_MS);
    return () => clearInterval(pollRef.current);
  }, [userId, refreshUnreadCount]);

  // ── load full notification list (called when panel opens) ─────────
  const loadNotifications = useCallback(
    async (page = 1) => {
      if (!userId) return;
      setLoading(true);
      try {
        const data = await getNotificationsApi(userId, { page, page_size: 20 });
        if (page === 1) {
          setNotifications(data.results || []);
        } else {
          setNotifications((prev) => [...prev, ...(data.results || [])]);
        }
        setUnreadCount(data.unread_count ?? 0);
        setPagination(data.pagination || {});
      } catch (_) {
        /* silent */
      } finally {
        setLoading(false);
      }
    },
    [userId]
  );

  // ── mark single read ──────────────────────────────────────────────
  const markRead = useCallback(
    async (notificationId) => {
      try {
        const updated = await markReadApi(notificationId);
        setNotifications((prev) =>
          prev.map((n) => (n.id === notificationId ? { ...n, ...updated } : n))
        );
        setUnreadCount((c) => Math.max(0, c - 1));
      } catch (_) {}
    },
    []
  );

  // ── mark all read ─────────────────────────────────────────────────
  const markAllRead = useCallback(async () => {
    if (!userId) return;
    try {
      await markAllReadApi(userId);
      setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch (_) {}
  }, [userId]);

  // ── soft-delete single ────────────────────────────────────────────
  const deleteNotification = useCallback(async (notificationId) => {
    try {
      await deleteNotificationApi(notificationId);
      setNotifications((prev) => prev.filter((n) => n.id !== notificationId));
      setUnreadCount((c) => Math.max(0, c - 1));
    } catch (_) {}
  }, []);

  // ── clear all ─────────────────────────────────────────────────────
  const clearAll = useCallback(async () => {
    if (!userId) return;
    try {
      await clearAllNotificationsApi(userId);
      setNotifications([]);
      setUnreadCount(0);
    } catch (_) {}
  }, [userId]);

  // ── open / close panel ────────────────────────────────────────────
  const openPanel = useCallback(() => {
    setPanelOpen(true);
    loadNotifications(1);
  }, [loadNotifications]);

  const closePanel = useCallback(() => setPanelOpen(false), []);

  const loadMore = useCallback(() => {
    if (pagination.has_next) {
      loadNotifications(pagination.page + 1);
    }
  }, [pagination, loadNotifications]);

  return (
    <NotificationContext.Provider
      value={{
        notifications,
        unreadCount,
        loading,
        panelOpen,
        pagination,
        openPanel,
        closePanel,
        markRead,
        markAllRead,
        deleteNotification,
        clearAll,
        loadMore,
        refreshUnreadCount,
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
};

const NOOP = () => {};
const DEFAULT_CTX = {
  notifications: [],
  unreadCount: 0,
  loading: false,
  panelOpen: false,
  pagination: { page: 1, page_size: 20, total_count: 0, total_pages: 1, has_next: false, has_previous: false },
  openPanel: NOOP,
  closePanel: NOOP,
  markRead: NOOP,
  markAllRead: NOOP,
  deleteNotification: NOOP,
  clearAll: NOOP,
  loadMore: NOOP,
  refreshUnreadCount: NOOP,
};

export const useNotifications = () => {
  const ctx = useContext(NotificationContext);
  return ctx || DEFAULT_CTX;
};

export default NotificationContext;
